/**
 * Mobile navigation rules — the per-role FIELD bottom bar, the office add-on
 * model, the Field/Office homing logic, and the hard 5-button cap. Pure
 * functions so the app and the tests share one source of truth.
 *
 * Mirrors fieldNav()/officeNav()/capNav()/withCompanyBrainTab() in the approved
 * demo (public/demo/field.html) and Tim's staff-role taxonomy (7/20).
 *
 * FIELD bars (per role, demo §1):
 *   supervisor / technician / salesperson → Jobs · Schedule · Stats · Settings
 *   operations_coordinator                → Jobs · Dispatch · Schedule ·
 *                                            Equipment · Settings
 *                                            (Equipment swapped IN, Stats OUT —
 *                                            the OC Equipment-for-Stats rule)
 *   contents_supervisor                   → Jobs · Schedule · Warehouse · Stats
 *                                            · Settings
 *   rebuild_manager                       → Jobs · Rebuild · Warehouse ·
 *                                            Settings (Rebuild TAKES OVER the
 *                                            Schedule slot; no Dispatch, no Stats)
 *   facilities_coordinator                → Jobs · Schedule · Checklists ·
 *                                            Settings
 *   leadership / admin                    → Jobs · Dispatch · Schedule · Stats ·
 *                                            Settings (admin = master key, but
 *                                            still capped ≤5; reaches everything
 *                                            else via the office drawer + the
 *                                            admin "view as role" preview)
 *
 * Company Brain rides the FIELD bar as a 5th button ONLY when the bar has ≤4
 * buttons AND no add-on already owns a slot (withCompanyBrainTab); otherwise it
 * lives in the Settings card. The hard cap (capNav, MAX_BOTTOM_BUTTONS=5) keeps
 * Jobs first + Settings last and can never render more than 5 buttons.
 *
 * ── Add-ons (the seam) ──
 * The demo lets a user stack add-ons on top of a base role, each contributing
 * one function. Interim native model (WP-01): add-ons are REAL permission flags
 * modeled as pure functions over the CURRENT profiles.staff_role_keys — an
 * add-on key present in staff_role_keys means an admin granted it (that IS the
 * real Supabase-driven gate, replacing the demo's client 5-tap unlock). The
 * WHOLE add-on model funnels through `addonsForRoles()` so a later migration to
 * dedicated profile columns/flags only touches that one function.
 */

export type MobileTab =
  | 'jobs' | 'dispatch' | 'schedule' | 'rebuild' | 'warehouse'
  | 'checklists' | 'equipment' | 'stats' | 'company-brain'
  | 'plumbers' | 'alerts' | 'settings';

/**
 * Canonical bottom-bar order. Every single-role bar is a subsequence of this,
 * and multi-role users get the union filtered into this order (then capped).
 * Note: `rebuild` sits where `schedule` would (RM takeover), `equipment` sits
 * where `stats` would (OC swap) — both are handled by which keys each role
 * contributes, not by reordering.
 */
const TAB_ORDER: MobileTab[] = [
  'jobs', 'dispatch', 'schedule', 'rebuild', 'warehouse',
  'checklists', 'equipment', 'stats', 'plumbers', 'alerts',
  'company-brain', 'settings',
];

/** Hard cap on bottom-bar buttons (Tim). No role + add-on combo ever exceeds this. */
export const MAX_BOTTOM_BUTTONS = 5;

/** The Company Brain ride-along tab (see withCompanyBrainTab). */
const COMPANY_BRAIN_TAB: MobileTab = 'company-brain';

/** The new staff-role keys that home into the FIELD app. */
export const FIELD_ROLE_KEYS = [
  'supervisor', 'technician', 'operations_coordinator',
  'contents_supervisor', 'rebuild_manager', 'facilities_coordinator',
  'salesperson', 'leadership', 'admin',
] as const;

/** Office-home staff roles (drawer app). */
export const OFFICE_ROLE_KEYS = ['office_admin', 'estimator', 'financier'] as const;

/**
 * Legacy `staff_role_keys` → new taxonomy (docs/parity/roles.md mapping).
 * Old keys keep working: crew/tech/foreman read as technician, pm and
 * ops_manager read as supervisor/operations_coordinator, sales as salesperson.
 * Unknown keys (incl. add-on keys) pass through untouched.
 *
 * NOTE: `collections` is NOT mapped here — it is an add-on key (see ADDON_KEYS)
 * that must survive normalization so the Collections add-on can be detected.
 * The office Collections *base* role is `financier`.
 */
const LEGACY_MAP: Record<string, string> = {
  crew: 'technician',
  tech: 'technician',
  foreman: 'technician',
  pm: 'supervisor',
  ops_manager: 'operations_coordinator',
  sales: 'salesperson',
  invoicing: 'estimator',
  office: 'office_admin',
  office_support: 'office_admin',
};

/** Map a raw staff_role_keys array onto the new taxonomy (dedup, order kept). */
export function normalizeStaffRoles(keys: readonly string[]): string[] {
  const out: string[] = [];
  for (const k of keys) {
    const mapped = LEGACY_MAP[k] ?? k;
    if (!out.includes(mapped)) out.push(mapped);
  }
  // The main admin sees any function in the app — admin implies leadership,
  // so every leadership-only view opens for admin without each screen checking.
  if (out.includes('admin') && !out.includes('leadership')) out.push('leadership');
  return out;
}

/* ══ Add-ons — the single seam ══
   [key, label, section, screen]. `section` = which base role stacks it; `screen`
   = the native route/tab it contributes. Sensitive add-ons expose company data
   and only surface when the person actually holds the key (admin-assigned). */
export type AddonSection = 'office' | 'sales';
export interface Addon {
  key: string;
  label: string;
  section: AddonSection;
  /** The native screen/tab the add-on contributes. */
  screen: string;
  sensitive: boolean;
}

export const ADDONS: readonly Addon[] = [
  { key: 'on_call_taker', label: 'On-Call Taker',   section: 'office', screen: 'dispatch-office', sensitive: false },
  { key: 'collections',   label: 'Collections',     section: 'office', screen: 'collections',     sensitive: true },
  { key: 'marketing',     label: 'Marketing',       section: 'office', screen: 'marketing',       sensitive: false },
  { key: 'leadership',    label: 'Leadership Team',  section: 'office', screen: 'company',         sensitive: true },
  { key: 'plumber_manager', label: 'Plumber Manager', section: 'sales', screen: 'plumbers',       sensitive: false },
  { key: 'alerts',        label: 'Alert Page',       section: 'sales', screen: 'alert-page',       sensitive: false },
] as const;

const ADDON_BY_KEY: Record<string, Addon> = Object.fromEntries(ADDONS.map((a) => [a.key, a]));

/** Add-on keys that expose sensitive company data (leadership, collections). */
export const SENSITIVE_ADDONS: readonly string[] = ADDONS.filter((a) => a.sensitive).map((a) => a.key);

/** True when the add-on key exposes sensitive company data. */
export function isSensitiveAddon(key: string): boolean {
  return ADDON_BY_KEY[key]?.sensitive ?? false;
}

/**
 * THE add-on seam. Which add-ons a user's raw staff_role_keys grant, in the
 * canonical ADDONS order. Detected from the RAW keys (an add-on key present =
 * an admin granted it = the real permission flag). `leadership` is also a
 * normalized role, so a normalized-away input still resolves it; `collections`
 * is deliberately kept out of LEGACY_MAP so it survives here.
 *
 * A later migration to dedicated `profiles` flags only has to change THIS body.
 */
export function addonsForRoles(rawKeys: readonly string[]): Addon[] {
  const present = new Set(rawKeys);
  // leadership is implied by admin (master key) — surface the add-on too.
  if (normalizeStaffRoles(rawKeys).includes('admin')) present.add('leadership');
  return ADDONS.filter((a) => present.has(a.key));
}

/** Active add-ons for one section (office | sales), in canonical order. */
export function addonsForSection(rawKeys: readonly string[], section: AddonSection): Addon[] {
  return addonsForRoles(rawKeys).filter((a) => a.section === section);
}

/** Per-role FIELD tab entitlements (see header comment; demo fieldNav). */
function fieldTabsForRole(role: string): MobileTab[] {
  switch (role) {
    case 'operations_coordinator':
      // Equipment IN, Stats OUT (the OC swap) — exactly 5 with Dispatch.
      return ['jobs', 'dispatch', 'schedule', 'equipment', 'settings'];
    case 'contents_supervisor':
      return ['jobs', 'schedule', 'warehouse', 'stats', 'settings'];
    case 'rebuild_manager':
      // Rebuild TAKES OVER the Schedule slot; keeps Warehouse; no Dispatch/Stats.
      return ['jobs', 'rebuild', 'warehouse', 'settings'];
    case 'facilities_coordinator':
      return ['jobs', 'schedule', 'checklists', 'settings'];
    case 'leadership':
    case 'admin':
      // Admin is the master key but the cap is HARD — this is its bar; the rest
      // is reached via the office drawer + the "view as role" preview.
      return ['jobs', 'dispatch', 'schedule', 'stats', 'settings'];
    case 'supervisor':
    case 'technician':
    case 'salesperson':
      return ['jobs', 'schedule', 'stats', 'settings'];
    default:
      return [];
  }
}

/**
 * Company Brain rides the FIELD bar as the 5th button when there's room (≤4
 * buttons) and no add-on already owns a bar slot; otherwise it stays in the
 * Settings card. Inserted just before Settings.
 */
function withCompanyBrainTab(nav: MobileTab[]): MobileTab[] {
  if (nav.includes(COMPANY_BRAIN_TAB)) return nav;
  if (nav.length > MAX_BOTTOM_BUTTONS - 1) return nav; // ≤4 → adding keeps ≤5
  // A sales add-on already owns a slot → keep the add-on model clean.
  if (nav.includes('plumbers') || nav.includes('alerts')) return nav;
  const out = nav.slice();
  const si = out.indexOf('settings');
  if (si >= 0) out.splice(si, 0, COMPANY_BRAIN_TAB);
  else out.push(COMPANY_BRAIN_TAB);
  return out;
}

/**
 * Safety net: even an unusual role + add-on stack can never render >5 buttons.
 * Keeps the anchors (Jobs first, Settings last) and the earliest middles.
 */
export function capNav(nav: MobileTab[]): MobileTab[] {
  if (nav.length <= MAX_BOTTOM_BUTTONS) return nav;
  const first = nav[0];
  const last = nav[nav.length - 1];
  const middle = nav.slice(1, -1).slice(0, MAX_BOTTOM_BUTTONS - 2);
  return [first, ...middle, last];
}

/**
 * The FIELD bottom bar for a user's staff_role_keys — union of their field
 * roles' tabs (canonical order) + stacked SALES add-on tabs (plumber_manager →
 * Plumbers, alerts → Alerts) for a salesperson, then the Company Brain
 * ride-along where there's room, then the hard cap. A user with no field role
 * still gets the base field bar when they flip into field mode from an office
 * role.
 */
export function mobileTabsForRoles(rawKeys: readonly string[]): MobileTab[] {
  const roles = normalizeStaffRoles(rawKeys);
  const allowed = new Set<MobileTab>();
  for (const r of roles) for (const t of fieldTabsForRole(r)) allowed.add(t);
  if (allowed.size === 0) {
    for (const t of ['jobs', 'schedule', 'stats', 'settings'] as MobileTab[]) allowed.add(t);
  }
  // Sales add-ons stack onto the salesperson base (demo §19).
  if (roles.includes('salesperson')) {
    for (const a of addonsForSection(rawKeys, 'sales')) {
      if (a.screen === 'plumbers') allowed.add('plumbers');
      if (a.screen === 'alert-page') allowed.add('alerts');
    }
  }
  let nav = TAB_ORDER.filter((t) => allowed.has(t));
  nav = withCompanyBrainTab(nav);
  return capNav(nav);
}

/** Which app a user's phone lands in: field bar or office drawer. */
export function appHomeForRoles(rawKeys: readonly string[]): 'field' | 'office' {
  const roles = normalizeStaffRoles(rawKeys);
  return roles.some((r) => (FIELD_ROLE_KEYS as readonly string[]).includes(r)) ? 'field' : 'office';
}

/**
 * Settings shows the Field ↔ Office mode toggle only for roles that genuinely
 * straddle both apps: operations_coordinator, rebuild_manager, facilities_
 * coordinator, leadership, estimator, admin — PLUS any office role carrying the
 * Leadership Team add-on (the office↔field straddle, demo canSwitchApp).
 */
export function canToggleAppMode(rawKeys: readonly string[]): boolean {
  const roles = normalizeStaffRoles(rawKeys);
  if (roles.some((r) =>
    r === 'operations_coordinator' || r === 'rebuild_manager'
    || r === 'facilities_coordinator' || r === 'leadership'
    || r === 'estimator' || r === 'admin')) return true;
  // Office base + Leadership Team add-on straddles too.
  return addonsForRoles(rawKeys).some((a) => a.key === 'leadership');
}

/** True when the user has any of the given (new-taxonomy) roles. */
export function hasStaffRole(rawKeys: readonly string[], ...roles: string[]): boolean {
  const norm = normalizeStaffRoles(rawKeys);
  return roles.some((r) => norm.includes(r));
}
